import React, { useState, useEffect } from "react";
import { Modal, Button, Input, Table, Tabs, Form, message } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from "../../hooks/useCategories";
import AddEditSubcategoryModal from "./AddEditSubcategoryModal"; // Import the updated modal

const { TabPane } = Tabs;

interface CategoryChild {
  id: number;
  name: string;
}

interface Category {
  id: number;
  name: string;
  image?: string; // Add image field for categories
  children: CategoryChild[];
}

interface ManageCategoriesModalProps {
  visible: boolean;
  onCancel: () => void;
  categories: Category[];
}

const ManageCategoriesModal: React.FC<ManageCategoriesModalProps> = ({
  visible,
  onCancel,
  categories,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState<string>(
    categories[0]?.name || ""
  );
  const [isAddEditModalVisible, setAddEditModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: number;
    name: string;
    isParent: boolean;
    image?: string; // Add image field for editing
  } | null>(null);
  const [isParentCategory, setIsParentCategory] = useState<boolean>(false); // Track if it's a parent category

  const { mutate: createCategory } = useCreateCategory();
  const { mutate: updateCategory } = useUpdateCategory();
  const { mutate: deleteCategory } = useDeleteCategory();

  const [form] = Form.useForm();

  // Set the first category as selected when the modal opens
  useEffect(() => {
    if (visible && categories.length > 0) {
      setSelectedTab(categories[0].name);
    }
  }, [visible, categories]);

  // Filter subcategories by selected tab and search term
  const filteredSubcategories =
    categories
      .find((category) => category.name === selectedTab)
      ?.children.filter((child) =>
        child.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) || [];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleAddNewCategory = (isParent: boolean) => {
    setSelectedCategory(null);
    form.resetFields();
    setAddEditModalVisible(true);
    setIsParentCategory(isParent); // Track if it's a parent category
  };

  const handleEditCategory = (
    id: number,
    name: string,
    isParent: boolean,
    image?: string
  ) => {
    setSelectedCategory({ id, name, isParent, image });
    form.setFieldsValue({ name });
    setAddEditModalVisible(true);
    setIsParentCategory(isParent); // Track if it's a parent category
  };

  const handleDeleteCategory = (id: number, isParent: boolean) => {
    Modal.confirm({
      title: `Are you sure you want to delete this ${
        isParent ? "category" : "subcategory"
      }?`,
      onOk: () => {
        deleteCategory(id, {
          onSuccess: () => {
            message.success(
              `${isParent ? "Category" : "Subcategory"} deleted successfully!`
            );
          },
          onError: () => {
            message.error(
              `Failed to delete ${
                isParent ? "category" : "subcategory"
              }. Please try again.`
            );
          },
        });
      },
    });
  };

  const handleAddEditSubmit = async (values: {
    name: string;
    image?: string;
  }) => {
    try {
      if (selectedCategory) {
        // Edit existing category or subcategory
        updateCategory(
          { id: selectedCategory.id, name: values.name, image: values.image },
          {
            onSuccess: () => {
              message.success(
                `${
                  selectedCategory.isParent ? "Category" : "Subcategory"
                } updated successfully!`
              );
              setAddEditModalVisible(false);
            },
            onError: () => {
              message.error(
                `Failed to update ${
                  selectedCategory.isParent ? "category" : "subcategory"
                }. Please try again.`
              );
            },
          }
        );
      } else {
        // Add new category or subcategory
        const categoryData = {
          name: values.name,
          ...(isParentCategory ? { image: values.image } : {}), // Include image only for parent categories
          ...(!isParentCategory && {
            parent_id: categories.find(
              (category) => category.name === selectedTab
            )?.id,
          }), // Include parent_id only for subcategories
        };

        createCategory(categoryData, {
          onSuccess: () => {
            message.success(
              `${
                isParentCategory ? "Category" : "Subcategory"
              } created successfully!`
            );
            setAddEditModalVisible(false);
          },
          onError: () => {
            message.error(
              `Failed to create ${
                isParentCategory ? "category" : "subcategory"
              }. Please try again.`
            );
          },
        });
      }
    } catch (error) {
      message.error("An error occurred. Please try again.");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: CategoryChild) => (
        <div className="flex space-x-2">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditCategory(record.id, record.name, false)}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteCategory(record.id, false)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Manage Categories"
        visible={visible}
        onCancel={onCancel}
        footer={null}
        width={800}
      >
        <div className="flex justify-between mb-4">
          <Input
            placeholder="Search subcategories"
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ width: "40%" }} // Reduced width for search field
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleAddNewCategory(true)}
          >
            Add Category
          </Button>
        </div>

        <Tabs activeKey={selectedTab} onChange={(key) => setSelectedTab(key)}>
          {categories.map((category) => (
            <TabPane
              tab={
                <div className="flex items-center">
                  <span>{category.name}</span>
                  <div className="ml-2 opacity-0 hover:opacity-100 transition-opacity duration-200">
                    <Button
                      icon={<EditOutlined />}
                      type="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCategory(
                          category.id,
                          category.name,
                          true,
                          category.image
                        );
                      }}
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      type="text"
                      danger
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(category.id, true);
                      }}
                    />
                  </div>
                </div>
              }
              key={category.name}
            >
              <div className="flex justify-between items-center mb-4">
                <span className="font-bold">Subcategories</span>
                <Button
                  type="default"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddNewCategory(false)}
                >
                  Add Subcategory
                </Button>
              </div>
              <Table
                columns={columns}
                dataSource={filteredSubcategories}
                rowKey="id"
                pagination={false}
              />
            </TabPane>
          ))}
        </Tabs>
      </Modal>

      {/* Add/Edit Category/Subcategory Modal */}
      <AddEditSubcategoryModal
        visible={isAddEditModalVisible}
        onCancel={() => setAddEditModalVisible(false)}
        onSubmit={handleAddEditSubmit}
        initialValues={
          selectedCategory
            ? { name: selectedCategory.name, image: selectedCategory.image }
            : undefined
        }
        isParent={isParentCategory} // Pass isParent prop
      />
    </>
  );
};

export default ManageCategoriesModal;
